import React, { useMemo } from 'react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import type { HeadType, RowType } from '@atlaskit/dynamic-table';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { JiraIcon } from '@atlaskit/logo';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { SupportedThirdParty } from '../../../../../common/constants/import-type';
import { renderIcon } from '../../../../../common/ui/icon';
import { AsyncMappingTable } from '../../../../../common/ui/mapping-table';
import { useStatusMappingEntityContext } from '../../../../../common/utils/status-mapping-entity-context';
import { useThirdPartyState } from '../../../../../controllers/import-state';
import { useImportTool } from '../../../../../controllers/utils';
import messages from './messages';
import { StatusSelector } from './status-selector';

type Props = {
	thirdParty: SupportedThirdParty;
};

export const StatusTable = ({ thirdParty }: Props) => {
	const tableHead = useStatusTableHeader(thirdParty);
	const tableRows = useStatusTableRows();

	return (
		<Box paddingBlockStart="space.100">
			<AsyncMappingTable
				head={tableHead}
				rows={tableRows}
				rowsPerPage={6}
				emptyMessage={messages.noStatus}
			/>
		</Box>
	);
};

export const useStatusTableHeader = (thirdParty: SupportedThirdParty) => {
	const { formatMessage } = useIntl();
	const tool = useImportTool(thirdParty);

	return useMemo<HeadType>(
		() => ({
			cells: [
				{
					key: 'thirdPartyStatus',
					content: (
						<Inline space="space.050" alignBlock="center">
							{tool && renderIcon(tool?.icon, true)}
							{`${tool?.name} ${formatMessage(messages.statusHeader)}`}
						</Inline>
					),
				},
				{
					key: 'arrow',
					width: 5,
				},
				{
					key: 'jiraStatus',
					content: (
						<Inline space="space.050" alignBlock="center">
							<JiraIcon size="small" appearance="brand" />
							{formatMessage(messages.jira)}
						</Inline>
					),
					width: 40,
				},
			],
		}),
		[formatMessage, tool],
	);
};

const useStatusTableRows = (): RowType[] | undefined => {
	const [{ thirdPartyStatusMapping }] = useThirdPartyState();
	const selectedEntity = useStatusMappingEntityContext();

	if (!thirdPartyStatusMapping || !selectedEntity) return undefined;

	const statusMappings =
		thirdPartyStatusMapping?.entityStatusMappings[selectedEntity].statusMappings;

	return Object.entries(statusMappings).map(([statusName, mappedStatus]) => ({
		key: statusName,
		cells: [
			{
				key: `${statusName}-thirdParty`,
				content: <Box xcss={thirdPartyStatusColumnStyle}>{statusName}</Box>,
			},
			{
				key: `${statusName}-arrow`,
				content: <ArrowRightIcon size="medium" label="Arrow Right" />,
			},
			{
				key: `${statusName}-jira`,
				content: (
					<StatusSelector selectedStatus={{ sourceStatusName: statusName, ...mappedStatus }} />
				),
			},
		],
	}));
};

const thirdPartyStatusColumnStyle = xcss({
	textTransform: 'capitalize',
});
